import React, {useEffect, useState} from "react"
import {Button, Layout as AntdLayout, Space} from "antd"
import {createUseStyles} from "react-jss"
import {useLocation, useNavigate} from "react-router-dom"
import Header, {ListProps} from "./header/Header"
import {useGetSelfEmployeeQuery, useUpdateEmployeeMutation} from "../store/api/employeeApi";
import {ClockCircleOutlined, FileMarkdownOutlined, InfoCircleOutlined, PrinterOutlined} from "@ant-design/icons";
import {ERROR, WARNING} from "../types/utils/Constants";
import LoadingBlock from "../components/loader/LoadingBlock";
import useWebSocket from "../configurations/webSocketConfig/useWebSocket";

const {Content} = AntdLayout

const useStyles = createUseStyles({
    layout: {
        background: "#ffffff"
    },
    content: {
        margin: "0",
        padding: 24,
        minHeight: 280
    },
    "@media (min-width: 1200px)": {
        content: {
            margin: "0",
            padding: 24,
            minHeight: 280
        }
    },
    warningTextBtnStyle: {
        backgroundColor: "#FFC429",
        color: "#ffffff"
    },
    warningCountBtnStyle: {color: "#FFC429"},
})


interface LayoutProps {
    children?: React.ReactNode | React.ReactNode[];
}

const Layout: React.FC<LayoutProps> = ({children}) => {    
    const classes = useStyles()
    const location = useLocation()
    const navigate = useNavigate()
    const {data} = useGetSelfEmployeeQuery()
    const notification = useWebSocket()
    const [updateEmployee, {isLoading}] = useUpdateEmployeeMutation()
    const {data: employee} = useGetSelfEmployeeQuery()


    const [activeWarning, setActiveWarning] = useState(false)
    const [printTypes, setPrintTypes] = useState<string[]>()
    const [extra, setExtra] = useState<any>()


    const list: ListProps[] = [
        {
            title: "Аккаунты",
            link: "/accounts",
            hidden: !employee?.data?.permission?.can_manage_account,
            hiddenInHeader: true,
        },
        {
            title: "Пользователи",
            link: "/employees",
            hidden: !employee?.data?.permission?.can_manage_account,
            hiddenInHeader: true,
        },
        {
            title: "Распечатка",
            link: "/",
            hidden: false,
            hiddenInHeader: true,
            icon: <PrinterOutlined/>
        },
        {
            title: "Маркировка",
            link: "/marking",
            hidden: false,
            hiddenInHeader: false,
            icon: <FileMarkdownOutlined/>
        },
        {
            title: "Категория печати",
            link: "/print-categories",
            hidden: !employee?.data?.permission?.can_manage_measurement,
            hiddenInHeader: true,
        },
        {
            title: "Производители",
            link: "/producers",
            hidden: !employee?.data?.permission?.can_manage_producer,
            hiddenInHeader: true,
        },
        {
            title: "Измерения",
            link: "/measurements",
            hidden: !employee?.data?.permission?.can_manage_measurement,
            hiddenInHeader: true,
        }
    ]

    const [section, setSection] = useState<ListProps | undefined>(list.filter(it => it.link === location.pathname)?.at(0))

    const toggleWarning = () => setActiveWarning(p => !p)


    useEffect(() => {
        setExtra(notifications(getCount(ERROR), getCount(WARNING)))
    }, [notification])


    useEffect(() => {
        document.body.scrollTop = 0
        document.documentElement.scrollTop = 0
    }, [location])

    const getCount = (type: string) => notification?.filter(item => item.type === type)?.at(0)?.count

    const warningBtnHandler = (type: string) => {
        setPrintTypes([type])
        toggleWarning()
    }

    const navigateTo = (link: string) => {
        setSection(list.filter(it => it.link === link)?.at(0))
        navigate(link)
    }

    const notifications = (errorCount: number | undefined, warningCount: number | undefined) => <Space>
        {errorCount && <Button.Group>
            <Button size={"large"} type='primary' danger icon={<InfoCircleOutlined/>}
                    shape={'round'} onClick={()=>navigateTo("/")}>
                Просроченные
            </Button>
            <Button size={"large"} danger shape={'round'} onClick={() => warningBtnHandler(ERROR)}>{errorCount}</Button>
        </Button.Group>}
        {warningCount && <Button.Group>
            <Button size={"large"} className={classes.warningTextBtnStyle} icon={<ClockCircleOutlined/>}
                    shape={'round'} onClick={()=>navigateTo("/")}>Истекают</Button>
            <Button size={"large"} className={classes.warningCountBtnStyle} shape={'round'}
                    onClick={() => warningBtnHandler(WARNING)}>{warningCount}</Button>
        </Button.Group>}
    </Space>


    if (isLoading) return <LoadingBlock/>

    return (
        <AntdLayout className={classes.layout}>
            <Header
                updateEmployee={updateEmployee}
                printTypes={printTypes}
                extra={extra}
                list={list}
                logoUrl={data?.data?.account?.picture_url ?? '/images/logo.png'}
                activeWarning={activeWarning}
                toggleWarning={toggleWarning}
                section={section}
                setSection={setSection}
            />
            <Content className={classes.content}>
                {children}
            </Content>
        </AntdLayout>
    )
}

export default Layout
